import React from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ListItemText as MuiListItemText, // Keep alias if used
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery, // Import useMediaQuery for responsiveness
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Import useTheme
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import VerifiedIcon from '@mui/icons-material/Verified';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import WarningIcon from '@mui/icons-material/Warning';

import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getAuth, onAuthStateChanged } from 'firebase/auth'; // Added onAuthStateChanged
import { 
  writeBatch, 
  serverTimestamp,
  collection, 
  getDocs, 
  query, 
  orderBy, 
  doc, 
  deleteDoc,
  where // <-- Make sure 'where' is imported
} from 'firebase/firestore'; 
import { firestore } from '../firebase.config'; 

// --- 1. IMPORT YOUR USER CONTEXT ---
import { useUserContext } from "./context/UserContext";

const ViewProducts = () => {
  // --- 2. GET ADMIN STATUS FROM THE GLOBAL CONTEXT ---
  const { isAdmin } = useUserContext();

  // --- 3. KEEP ONLY currentUser STATE ---
  const [currentUser, setCurrentUser] = React.useState(null); // Track current user

  // --- (userRole and isUserRoleLoading state has been REMOVED) ---

  // --- Responsive state ---
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State management
  const [importMode, setImportMode] = React.useState('standard'); // 'standard' or 'derived'
  const [availableProducts, setAvailableProducts] = React.useState([]);
  const [unifiedImportMode, setUnifiedImportMode] = React.useState(true); // New unified mode
  const [processingOrder, setProcessingOrder] = React.useState([]);

  const [products, setProducts] = React.useState([]);
  const [filteredProducts, setFilteredProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [viewMode, setViewMode] = React.useState('grid'); // 'grid' or 'list'
  
   // --- Add state for the bulk import feature ---
  const [bulkDialogOpen, setBulkDialogOpen] = React.useState(false);
  const [errors, setErrors] = React.useState([]);
  const [successMessage, setSuccessMessage] = React.useState('');
  const fileInputRef = React.useRef(null);
  // Individual delete states
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [productToDelete, setProductToDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [showAllProducts, setShowAllProducts] = React.useState(false);

  const navigate = useNavigate();

  // --- (fetchUserRole and fetchUserRoleSimple functions have been REMOVED) ---

  // --- 4. SIMPLIFIED AUTH LISTENER ---
  React.useEffect(() => {
    const auth = getAuth();
    console.log('Setting up auth state listener...');
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', user ? `User: ${user.email}` : 'No user');
      setCurrentUser(user);
      // (No need to fetch role here anymore)
    });

    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
    };
  }, []); // <-- Empty dependency array is correct

  // --- 5. SIMPLIFIED hasAdminAccess COMPUTED VALUE ---
  const hasAdminAccess = currentUser && isAdmin;
  
  // Add this debug useEffect to track dialog state changes
  React.useEffect(() => {
    console.log('📋 bulkDialogOpen state changed to:', bulkDialogOpen);
  }, [bulkDialogOpen]);

  // Debug logging for role state
  React.useEffect(() => {
    console.log('Admin access state changed:', {
      isAdmin_from_context: isAdmin,
      hasAdminAccess,
      currentUser: currentUser?.email || 'none',
    });
  }, [isAdmin, hasAdminAccess, currentUser]);

const handleAddProductClick = () => {
  if (!currentUser) {
    alert('Please log in to access this feature');
    return;
  }
  
  // (isUserRoleLoading check REMOVED)
  
  if (!hasAdminAccess) {
    alert('Access denied: Admin privileges required');
    return;
  }
  
  navigate('/AddProduct');
};

  // Filter states
  const [priceMenuAnchor, setPriceMenuAnchor] = React.useState(null);
  const [gstMenuAnchor, setGstMenuAnchor] = React.useState(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = React.useState(null);
  const [sourceMenuAnchor, setSourceMenuAnchor] = React.useState(null);

  const [selectedPriceRange, setSelectedPriceRange] = React.useState('');
  const [selectedGstValue, setSelectedGstValue] = React.useState('');
  const [selectedType, setSelectedType] = React.useState('');
  const [selectedSource, setSelectedSource] = React.useState('');

  // Filter options
  const priceRanges = [
    { value: '0-20', label: '₹0 - ₹20', min: 0, max: 20 },
    { value: '21-40', label: '₹21 - ₹40', min: 21, max: 40 },
    { value: '41-60', label: '₹41 - ₹60', min: 41, max: 60 },
    { value: '61-80', label: '₹61 - ₹80', min: 61, max: 80 },
    { value: '81-100', label: '₹81 - ₹100', min: 81, max: 100 },
    { value: '100+', label: '₹100+', min: 100, max: Infinity }
  ];

  const gstOptions = [
    { value: '0', label: '0% GST' },
    { value: '5', label: '5% GST' },
    { value: '12', label: '12% GST' },
    { value: '18', label: '18% GST' },
    { value: '28', label: '28% GST' }
  ];

  const typeOptions = [
    { value: 'standard', label: 'Standard Products', color: 'success' },
    { value: 'derived', label: 'Derived Products', color: 'warning' }
  ];

  const sourceOptions = [
    { value: 'main', label: 'Main Collection', color: 'primary' },
    { value: 'sub', label: 'Sub Collections', color: 'secondary' }
  ];

  // Individual delete functions (admin only)
const handleDeleteProduct = (product) => {
  if (!currentUser) {
    alert('Please log in to access this feature');
    return;
  }
  
  // (isUserRoleLoading check REMOVED)
  
  if (!hasAdminAccess) {
    alert('Access denied: Admin privileges required');
    return;
  }
  
  setProductToDelete(product);
  setShowConfirmDialog(true);
};
  
const handleBulkOpen = () => {
  console.log('=== Bulk Import Button Clicked ===');
  console.log('currentUser:', currentUser?.email || 'none');
  console.log('isAdmin (from context):', isAdmin);
  console.log('hasAdminAccess (computed):', hasAdminAccess);
  
  // Prevent multiple rapid clicks
  if (bulkDialogOpen) {
    console.log('Dialog already open, ignoring click');
    return;
  }
  
  if (!currentUser) {
    console.log('❌ No current user');
    alert('Please log in to access this feature');
    return;
  }
  
  // (isUserRoleLoading check REMOVED)
  
  // --- 6. UPDATED CHECK to use hasAdminAccess ---
  if (!hasAdminAccess) {
    console.log('❌ Access denied based on `hasAdminAccess` check.');
    alert('Access denied: Admin privileges required');
    return;
  }
  
  console.log('✅ All checks passed - opening dialog');
  
  // Clear states first
  setErrors([]);
  setSuccessMessage('');
  setImportMode('standard');
  
  // Open dialog with small delay to ensure state is set
  setTimeout(() => {
    console.log('Setting bulkDialogOpen to true');
  setBulkDialogOpen(true);
  loadAvailableProducts();
  }, 10);
};


const loadAvailableProducts = async () => {
  try {
    const catalogueRef = collection(firestore, "Catalogue");
    const q = query(catalogueRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    
    const products = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Only include Level1 products for component selection
      if (data.isLevel1 === true || !data.parentId) {
        products.push({
          id: doc.id,
          name: data.name,
          price: data.price,
          isStandard: data.isStandard,
          ...data
        });
      }
    });
    
    setAvailableProducts(products);
    console.log(`Loaded ${products.length} available products for derived product creation`);
  } catch (error) {
    console.error("Error loading available products:", error);
  }
};

 const handleBulkClose = () => {
  console.log('🚪 Closing bulk dialog');
  setBulkDialogOpen(false);
};


 const handleDownloadTemplate = () => {
  if (!hasAdminAccess) {
    console.log('Access denied: Admin access =', hasAdminAccess);
    alert('Access denied: Admin privileges required');
    return;
  }

  let templateHeaders, filename, worksheetName;
  
      if (unifiedImportMode) {
    // Unified template with both standard and derived products - headers only
    templateHeaders = [
      [
        "Product_Type", // "standard" or "derived" 
        "Name", 
        "Description",
        "Cost", // New column: cost (numeric)
        "Margin", // New column: margin % (numeric)
        "Price", // Only for standard products, leave empty for derived (auto-calculated) - price will be computed from cost+margin when provided
        "GST", 
        "HSN", 
        "MOQ",
        "Component1_Name", // For derived products
        "Component1_Quantity",
        "Component2_Name",
        "Component2_Quantity", 
        "Component3_Name",
        "Component3_Quantity",
        "Component4_Name", 
        "Component4_Quantity",
        "Component5_Name",
        "Component5_Quantity"
      ]
    ];
    filename = "unified_products_template.xlsx";
    worksheetName = "Products";
  } else if (importMode === 'standard') {
    // Standard products template - headers only
    templateHeaders = [
      ["Name", "Description", "Cost", "Margin", "Price", "GST", "HSN", "MOQ"]
    ];
    filename = "standard_products_template.xlsx";
    worksheetName = "StandardProducts";
    } else {
    // Derived products template - headers only (include Price column for optional override)
    templateHeaders = [
      ["Name", "Description", "Cost", "Margin", "Price", "GST", "HSN", "MOQ", "Component1_Name", "Component1_Quantity", "Component2_Name", "Component2_Quantity", "Component3_Name", "Component3_Quantity", "Component4_Name", "Component4_Quantity", "Component5_Name", "Component5_Quantity"]
    ];
    filename = "derived_products_template.xlsx";
    worksheetName = "DerivedProducts";
  }
  
  const ws = XLSX.utils.aoa_to_sheet(templateHeaders);
  
  // Set column widths for better readability
  const columnWidths = templateHeaders[0].map((header) => {
    if (header.includes('Name')) return { wch: 25 };
    if (header.includes('Description')) return { wch: 30 };
    if (header.includes('Component')) return { wch: 20 };
    if (header.includes('Product_Type')) return { wch: 15 };
    if (header === 'Cost' || header === 'Price') return { wch: 12 };
    if (header === 'Margin') return { wch: 10 };
    return { wch: 12 };
  });
  ws['!cols'] = columnWidths;
  
  // Add some styling to make headers stand out
  const headerRange = XLSX.utils.decode_range(ws['!ref']);
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellAddress]) continue;
    
    // Make headers bold and add background color
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "E3F2FD" } }, // Light blue background
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
  }
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, worksheetName);
  
  // Add some metadata to the workbook
  wb.Props = {
    Title: `${unifiedImportMode ? 'Unified' : importMode === 'standard' ? 'Standard' : 'Derived'} Products Import Template`,
    Subject: "Product Import Template",
    Author: "Product Management System",
    CreatedDate: new Date()
  };
  
  XLSX.writeFile(wb, filename);
};
  
  const handleFileUpload = async (e) => {
  if (!hasAdminAccess) {
    console.log('Access denied: Admin access =', hasAdminAccess);
    alert('Access denied: Admin privileges required');
    return;
  }

  setErrors([]);
  setSuccessMessage('');
  const file = e.target.files?.[0];
  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
  if (!file) return;

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

    if (rows.length === 0) {
      setErrors(["The uploaded file is empty."]);
      return;
    }

    if (unifiedImportMode) {
      await processUnifiedImport(rows);
    } else {
      // Fall back to existing separate processing
      if (importMode === 'standard') {
        await processStandardProducts(rows);
      } else {
        await processDerivedProducts(rows);
      }
    }

  } catch (err) {
    console.error("Bulk upload failed:", err);
    setErrors([err.message || "An unexpected error occurred during import."]);
  }
};

  const processStandardProducts = async (rows) => {
  const validationErrors = [];
  const existingNames = new Set(products.map(p => p.name.toLowerCase()));

  // Validate standard products
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    const name = (row["Name"] || "").trim();

    if (!name) {
      validationErrors.push(`Row ${rowNum}: "Name" is required.`);
    } else if (existingNames.has(name.toLowerCase())) {
      validationErrors.push(`Row ${rowNum}: Product "${name}" already exists.`);
    }

    // Accept either Cost+Margin or Price. Prefer Cost+Margin when provided.
    const hasCost = row["Cost"] !== undefined && row["Cost"] !== "";
    const hasMargin = row["Margin"] !== undefined && row["Margin"] !== "";
    const hasPrice = row["Price"] !== undefined && row["Price"] !== "";

    if (hasCost && hasMargin) {
      if (isNaN(Number(row["Cost"]))) {
        validationErrors.push(`Row ${rowNum}: "Cost" must be a valid number.`);
      }
      if (isNaN(Number(row["Margin"]))) {
        validationErrors.push(`Row ${rowNum}: "Margin" must be a valid number.`);
      }
    } else if (hasPrice) {
      if (isNaN(Number(row["Price"]))) {
        validationErrors.push(`Row ${rowNum}: "Price" must be a valid number.`);
      }
    } else {
      validationErrors.push(`Row ${rowNum}: Provide either Cost+Margin or Price for standard product.`);
    }

    if (isNaN(Number(row["GST"]))) {
      validationErrors.push(`Row ${rowNum}: "GST" must be a valid number.`);
    }

    // If Price is provided for derived product, ensure it's numeric and positive
    if (row["Price"] !== undefined && row["Price"] !== "") {
      if (isNaN(Number(row["Price"])) || Number(row["Price"]) <= 0) {
        validationErrors.push(`Row ${rowNum}: "Price" must be a positive number when provided for derived product.`);
      }
    }
  });

  if (validationErrors.length > 0) {
    setErrors(validationErrors);
    return;
  }

  // Create standard products
  const batch = writeBatch(firestore);
  rows.forEach(row => {
    const docRef = doc(collection(firestore, "Catalogue"));

    // Determine cost/margin/price
    const costProvided = row["Cost"] !== undefined && row["Cost"] !== "";
    const marginProvided = row["Margin"] !== undefined && row["Margin"] !== "";
    let costVal = costProvided ? Number(row["Cost"]) : 0;
    let marginVal = marginProvided ? Number(row["Margin"]) : 0;
    let computedPrice = 0;

    if (costProvided && marginProvided) {
      computedPrice = costVal * (1 + (marginVal / 100));
    } else if (row["Price"] !== undefined && row["Price"] !== "") {
      computedPrice = Number(row["Price"] || 0);
      // keep cost/margin if provided partially
      costVal = costVal || 0;
      marginVal = marginVal || 0;
    }

    batch.set(docRef, {
      name: row["Name"].trim(),
      description: row["Description"] || "",
      price: Number(computedPrice),
      cost: Number(costVal || 0),
      margin: Number(marginVal || 0),
      gst: Number(row["GST"] || 0),
      hsn: String(row["HSN"] || ""),
      moq: Number(row["MOQ"] || 0),
      isStandard: true,
      isLevel1: true,
      hasSubProducts: false,
      createdAt: serverTimestamp(),
    });
  });

  await batch.commit();
  setSuccessMessage(`${rows.length} new standard products were successfully imported!`);
  fetchAllProducts();
};

const processUnifiedImport = async (rows) => {
  const validationErrors = [];
  const existingNames = new Set(products.map(p => p.name.toLowerCase()));
  const standardProducts = [];
  const derivedProducts = [];
  const allProductNames = new Set(existingNames); // Track all names including new ones being added

  // Step 1: Separate and validate products
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    const productType = (row["Product_Type"] || "").trim().toLowerCase();
    const name = (row["Name"] || "").trim();

    // Validate product type
    if (!productType || !['standard', 'derived'].includes(productType)) {
      validationErrors.push(`Row ${rowNum}: Product_Type must be either "standard" or "derived".`);
      return;
    }

    // Validate name uniqueness
    if (!name) {
      validationErrors.push(`Row ${rowNum}: "Name" is required.`);
    } else if (allProductNames.has(name.toLowerCase())) {
      validationErrors.push(`Row ${rowNum}: Product "${name}" already exists or is duplicated in this import.`);
    } else {
      allProductNames.add(name.toLowerCase());
    }

    // Common validations
    if (isNaN(Number(row["GST"]))) {
      validationErrors.push(`Row ${rowNum}: "GST" must be a valid number.`);
    }

    // Type-specific validation
    if (productType === 'standard') {
      if (isNaN(Number(row["Price"])) || Number(row["Price"]) <= 0) {
        validationErrors.push(`Row ${rowNum}: Standard product "Price" must be a positive number.`);
      }
      standardProducts.push({ ...row, rowNum, productType: 'standard' });
    } else if (productType === 'derived') {
      // Validate derived product components
      let hasComponents = false;
      for (let i = 1; i <= 5; i++) {
        const componentName = (row[`Component${i}_Name`] || "").trim();
        const componentQuantity = row[`Component${i}_Quantity`];

        if (componentName) {
          hasComponents = true;
          if (isNaN(Number(componentQuantity)) || Number(componentQuantity) <= 0) {
            validationErrors.push(`Row ${rowNum}: Component "${componentName}" quantity must be a positive number.`);
          }
        }
      }

      if (!hasComponents) {
        validationErrors.push(`Row ${rowNum}: Derived product "${name}" must have at least one component.`);
      }
      derivedProducts.push({ ...row, rowNum, productType: 'derived' });
    }
  });

  if (validationErrors.length > 0) {
    setErrors(validationErrors);
    return;
  }

  // Step 2: Determine processing order (standard products first, then derived products in dependency order)
  const processingOrder = [...standardProducts];
  
  // For derived products, we need to ensure dependencies are processed first
  const sortedDerivedProducts = sortDerivedProductsByDependency(derivedProducts, allProductNames);
  processingOrder.push(...sortedDerivedProducts);

  setProcessingOrder(processingOrder);

  // Step 3: Process products in order
  await processBatchInOrder(processingOrder);
};
const sortDerivedProductsByDependency = (derivedProducts, allProductNames) => {
  const availableProductsMap = new Map(availableProducts.map(p => [p.name.toLowerCase(), p]));
  const newProductsMap = new Map(); // Track new products being added
  const sorted = [];
  const remaining = [...derivedProducts];
  const processed = new Set();

  // Add existing products to available
  products.forEach(p => {
    availableProductsMap.set(p.name.toLowerCase(), p);
  });

  let iterations = 0;
  const maxIterations = derivedProducts.length * 2; // Prevent infinite loops

  while (remaining.length > 0 && iterations < maxIterations) {
    let madeProgress = false;

    for (let i = remaining.length - 1; i >= 0; i--) {
      const product = remaining[i];
      const canProcess = checkIfCanProcess(product, availableProductsMap, newProductsMap);

      if (canProcess) {
        sorted.push(product);
        newProductsMap.set(product.Name.toLowerCase(), { 
          name: product.Name, 
          isStandard: false 
        });
        remaining.splice(i, 1);
        madeProgress = true;
      }
    }

    if (!madeProgress) {
      // If we can't make progress, there might be circular dependencies
      const remainingNames = remaining.map(p => p.Name);
      setErrors([`Circular dependency detected or missing components for: ${remainingNames.join(', ')}`]);
      return [];
    }

    iterations++;
  }

  return sorted;
};
const processDerivedProducts = async (rows) => {
  const validationErrors = [];
  const existingNames = new Set(products.map(p => p.name.toLowerCase()));
  const availableProductsMap = new Map(availableProducts.map(p => [p.name.toLowerCase(), p]));

  // Validate derived products
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    const name = (row["Name"] || "").trim();

    if (!name) {
      validationErrors.push(`Row ${rowNum}: "Name" is required.`);
    } else if (existingNames.has(name.toLowerCase())) {
      validationErrors.push(`Row ${rowNum}: Product "${name}" already exists.`);
    }
    if (isNaN(Number(row["GST"]))) {
      validationErrors.push(`Row ${rowNum}: "GST" must be a valid number.`);
    }

    // Validate components
    let hasComponents = false;
    for (let i = 1; i <= 5; i++) {
      const componentName = (row[`Component${i}_Name`] || "").trim();
      const componentQuantity = row[`Component${i}_Quantity`];

      if (componentName) {
        hasComponents = true;
        if (!availableProductsMap.has(componentName.toLowerCase())) {
          validationErrors.push(`Row ${rowNum}: Component "${componentName}" not found in existing products.`);
        }
        if (isNaN(Number(componentQuantity)) || Number(componentQuantity) <= 0) {
          validationErrors.push(`Row ${rowNum}: Component "${componentName}" quantity must be a positive number.`);
        }
      }
    }

    if (!hasComponents) {
      validationErrors.push(`Row ${rowNum}: At least one component is required for derived product "${name}".`);
    }
  });

  if (validationErrors.length > 0) {
    setErrors(validationErrors);
    return;
  }

  // Create derived products
  const batch = writeBatch(firestore);
  const processedProducts = [];

  for (const row of rows) {
    const components = [];
    let calculatedPrice = 0;

    // Process components
    for (let i = 1; i <= 5; i++) {
      const componentName = (row[`Component${i}_Name`] || "").trim();
      const componentQuantity = Number(row[`Component${i}_Quantity`] || 0);

      if (componentName && componentQuantity > 0) {
        const componentProduct = availableProductsMap.get(componentName.toLowerCase());
        if (componentProduct) {
          components.push({
            productId: componentProduct.id,
            name: componentProduct.name,
            quantity: componentQuantity,
            unitPrice: componentProduct.price,
            totalPrice: componentProduct.price * componentQuantity,
            isStandard: componentProduct.isStandard
          });
          calculatedPrice += componentProduct.price * componentQuantity;
        }
      }
    }

    const docRef = doc(collection(firestore, "Catalogue"));

    // Decide final price: Price override (if provided) > Margin-applied > calculated from components
    let finalDerivedPrice;
    if (row["Price"] !== undefined && row["Price"] !== "") {
      finalDerivedPrice = Number(row["Price"]);
    } else if (row["Margin"] !== undefined && row["Margin"] !== "") {
      finalDerivedPrice = calculatedPrice * (1 + (Number(row["Margin"]) / 100));
    } else {
      finalDerivedPrice = calculatedPrice;
    }

    const derivedProductData = {
      name: row["Name"].trim(),
      description: row["Description"] || "",
      // computed cost from components
      cost: Number(calculatedPrice || 0),
      // store margin if provided (0 otherwise)
      margin: Number(row["Margin"] || 0),
      // final price (override or computed)
      price: Number(finalDerivedPrice || 0),
      gst: Number(row["GST"] || 0),
      hsn: String(row["HSN"] || ""),
      moq: Number(row["MOQ"] || 0),
      isStandard: false, // Derived product
      isLevel1: true,
      hasSubProducts: true, // Derived products have components
      components: components,
      componentCount: components.length,
      createdAt: serverTimestamp(),
    };

    batch.set(docRef, derivedProductData);
    processedProducts.push(derivedProductData);
  }

  await batch.commit();
  setSuccessMessage(`${rows.length} new derived products were successfully imported with ${processedProducts.reduce((sum, p) => sum + p.componentCount, 0)} total components!`);
  fetchAllProducts();
};
const checkIfCanProcess = (derivedProduct, availableProductsMap, newProductsMap) => {
  for (let i = 1; i <= 5; i++) {
    const componentName = (derivedProduct[`Component${i}_Name`] || "").trim();
    if (componentName) {
      const componentExists = availableProductsMap.has(componentName.toLowerCase()) || 
                            newProductsMap.has(componentName.toLowerCase());
      if (!componentExists) {
        return false;
      }
    }
  }
  return true;
};
  const handleConfirmDelete = async () => {
    if (!productToDelete || !hasAdminAccess) return;
    
    setDeleting(true);
    try {
      // Delete from Firestore - only handling main collection for Level1 products
      const docRef = doc(firestore, 'Catalogue', productToDelete.id);
      await deleteDoc(docRef);
      
      // Update local state
      const updatedProducts = products.filter(p => p.id !== productToDelete.id);
      setProducts(updatedProducts);
      
      // Reapply filters to filteredProducts
      setFilteredProducts(prevFiltered => prevFiltered.filter(p => p.id !== productToDelete.id));

      console.log(`Successfully deleted Level1 product: ${productToDelete.name}`);
    } catch (error) {
      console.error('Error deleting Level1 product:', error);
      // You might want to show an error message to the user here
    } finally {
      setDeleting(false);
      setShowConfirmDialog(false);
      setProductToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setProductToDelete(null);
  };
  
  const fetchAllProducts = React.useCallback(async () => {
    setLoading(true);
    try {
      const productsFromDb = [];
      const catalogueRef = collection(firestore, "Catalogue");
      const q = query(catalogueRef, orderBy("createdAt", "desc"));
      const mainDocsSnapshot = await getDocs(q);
  
      console.log(`Found ${mainDocsSnapshot.docs.length} documents in Catalogue collection`);
  
      for (const mainDoc of mainDocsSnapshot.docs) {
        const mainData = mainDoc.data();
        const isLevel1 = mainData.isLevel1 === true || !mainData.parentId;
  
        if (isLevel1) {
          let productData = {
            id: mainDoc.id,
            source: 'main_collection',
            ...mainData
          };
  
          // If the product is marked as having sub-products, fetch them
          if (mainData.hasSubProducts) {
            // FOR DERIVED PRODUCTS (created via AddProduct or Bulk Upload)
            if (!mainData.isStandard) {
              if (mainData.components && mainData.components.length > 0) {
                // Case 1: Components array exists (from bulk import). Use it directly.
                productData.components = mainData.components;
                console.log(`Loaded ${mainData.components.length} components for bulk-imported product: ${mainData.name}`);
              } else {
                // Case 2: No components array (from AddProduct form). Fetch from 'SubCollection1'.
                try {
                  const subCollectionRef = collection(firestore, "Catalogue", mainDoc.id, "SubCollection1");
                  const subCollectionSnapshot = await getDocs(subCollectionRef);
                  
                  if (!subCollectionSnapshot.empty) {
                    const components = [];
                    subCollectionSnapshot.forEach((subDoc) => {
                      const subData = subDoc.data();
                      components.push({
                        productId: subDoc.id,
                        name: subData.name,
                        quantity: subData.quantity || 1, // Default quantity
                        unitPrice: subData.price || 0,
                        isStandard: subData.isStandard
                      });
                    });
                    productData.components = components;
                    console.log(`Loaded ${components.length} sub-products from SubCollection1 for derived product: ${mainData.name}`);
                  }
                } catch (error) {
                  console.error(`Error loading sub-products for derived product ${mainDoc.id}:`, error);
                }
              }
            } 
            // FOR STANDARD PRODUCTS THAT HAVE SUB-PRODUCTS
            else if (mainData.isStandard) {
              try {
                const subCollectionRef = collection(firestore, "Catalogue", mainDoc.id, "SubCollection1");
                const subCollectionSnapshot = await getDocs(subCollectionRef);
                
                if (!subCollectionSnapshot.empty) {
                  const subProductNames = [];
                  subCollectionSnapshot.forEach((subDoc) => {
                    subProductNames.push(subDoc.data().name);
                  });
                  productData.subProductNames = subProductNames;
                  console.log(`Loaded ${subProductNames.length} sub-product names for standard product: ${mainData.name}`);
                }
              } catch (error) {
                console.error(`Error loading sub-product names for standard product ${mainDoc.id}:`, error);
              }
            }
          }
  
          productsFromDb.push(productData);
        } else {
          console.log(`Excluded Level2+ product: ${mainData.name} (ID: ${mainDoc.id})`);
        }
      }
  
      console.log(`Total Level1 products loaded: ${productsFromDb.length}`);
      setProducts(productsFromDb);
      setFilteredProducts(productsFromDb);
  
    } catch (error) {
      console.error("Error fetching Level1 products from Firestore:", error);
    } finally {
      setLoading(false);
    }
  }, []);


const processBatchInOrder = async (orderedProducts) => {
  const batch = writeBatch(firestore);
  const processedCount = { standard: 0, derived: 0 };
  const availableProductsMap = new Map(availableProducts.map(p => [p.name.toLowerCase(), p]));
  const newlyCreatedProducts = new Map(); // Track products created in this batch

  // Add existing products to available map
  products.forEach(p => {
    availableProductsMap.set(p.name.toLowerCase(), p);
  });

  for (const productData of orderedProducts) {
    const docRef = doc(collection(firestore, "Catalogue"));

    if (productData.productType === 'standard') {
      // Determine cost/margin/price for standard product
      const costProvided = productData["Cost"] !== undefined && productData["Cost"] !== "";
      const marginProvided = productData["Margin"] !== undefined && productData["Margin"] !== "";
      let costVal = costProvided ? Number(productData["Cost"]) : 0;
      let marginVal = marginProvided ? Number(productData["Margin"]) : 0;
      let finalPrice = 0;

      if (costProvided && marginProvided) {
        finalPrice = costVal * (1 + (marginVal / 100));
      } else if (productData["Price"] !== undefined && productData["Price"] !== "") {
        finalPrice = Number(productData["Price"] || 0);
      }

      const standardProductData = {
        name: productData["Name"].trim(),
        description: productData["Description"] || "",
        price: Number(finalPrice),
        cost: Number(costVal || 0),
        margin: Number(marginVal || 0),
        gst: Number(productData["GST"] || 0),
        hsn: String(productData["HSN"] || ""),
        moq: Number(productData["MOQ"] || 0),
        isStandard: true,
        isLevel1: true,
        hasSubProducts: false,
        createdAt: serverTimestamp(),
      };

      batch.set(docRef, standardProductData);
      
      // Add to available products for derived products that might reference it
      availableProductsMap.set(productData["Name"].toLowerCase(), {
        id: docRef.id,
        name: productData["Name"],
        price: Number(finalPrice || 0),
        isStandard: true,
        ...standardProductData
      });

      processedCount.standard++;

    } else if (productData.productType === 'derived') {
      const components = [];
      let calculatedPrice = 0;

      // Process components
      for (let i = 1; i <= 5; i++) {
        const componentName = (productData[`Component${i}_Name`] || "").trim();
        const componentQuantity = Number(productData[`Component${i}_Quantity`] || 0);

        if (componentName && componentQuantity > 0) {
          const componentProduct = availableProductsMap.get(componentName.toLowerCase());
          if (componentProduct) {
            components.push({
              productId: componentProduct.id,
              name: componentProduct.name,
              quantity: componentQuantity,
              unitPrice: componentProduct.price,
              totalPrice: componentProduct.price * componentQuantity,
              isStandard: componentProduct.isStandard
            });
            calculatedPrice += componentProduct.price * componentQuantity;
          }
        }
      }

      // Allow optional margin/cost for derived products: computedCost is sum of component prices

      const derivedCost = calculatedPrice;
      const marginProvidedForDerived = productData["Margin"] !== undefined && productData["Margin"] !== "";
      const marginValForDerived = marginProvidedForDerived ? Number(productData["Margin"]) : 0;

      // Price override for derived products: prefer Price if provided, else apply Margin if provided, else use cost
      let finalDerivedPrice;
      if (productData["Price"] !== undefined && productData["Price"] !== "") {
        finalDerivedPrice = Number(productData["Price"]);
      } else if (marginProvidedForDerived) {
        finalDerivedPrice = derivedCost * (1 + (marginValForDerived / 100));
      } else {
        finalDerivedPrice = derivedCost;
      }

      const derivedProductData = {
        name: productData["Name"].trim(),
        description: productData["Description"] || "",
        price: finalDerivedPrice, // Auto-calculated from components and optional margin or override
        cost: Number(derivedCost || 0),
        margin: Number(marginValForDerived || 0),
        gst: Number(productData["GST"] || 0),
        hsn: String(productData["HSN"] || ""),
        moq: Number(productData["MOQ"] || 0),
        isStandard: false, // Derived product
        isLevel1: true,
        hasSubProducts: true,
        components: components,
        componentCount: components.length,
        createdAt: serverTimestamp(),
      };

      batch.set(docRef, derivedProductData);
      
      // Add to available products for other derived products that might reference it
      availableProductsMap.set(productData["Name"].toLowerCase(), {
        id: docRef.id,
        name: productData["Name"],
        price: finalDerivedPrice,
        isStandard: false,
        ...derivedProductData
      });

      processedCount.derived++;
    }
  }

  // Commit all products in a single batch
  await batch.commit();

  // Success message
  const totalComponents = orderedProducts
    .filter(p => p.productType === 'derived')
    .reduce((sum, p) => {
      let componentCount = 0;
      for (let i = 1; i <= 5; i++) {
        if ((p[`Component${i}_Name`] || "").trim()) componentCount++;
      }
      return sum + componentCount;
    }, 0);

  setSuccessMessage(
    `Successfully imported ${processedCount.standard + processedCount.derived} products: ` +
    `${processedCount.standard} standard products and ${processedCount.derived} derived products ` +
    `with ${totalComponents} total components!`
  );

  fetchAllProducts(); // Refresh the product list
};

  // Load user role and products on component mount
  React.useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  React.useEffect(() => {
    let filtered = [...products];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(search) ||
        (product.description && product.description.toLowerCase().includes(search)) ||
        (product.gst && product.gst.toString().includes(search)) ||
        (product.hsn && product.hsn.toString().includes(search)) ||
        (product.price && product.price.toString().includes(search))
      );
    }

    // Apply price filter
    if (selectedPriceRange) {
      const priceRange = priceRanges.find(p => p.value === selectedPriceRange);
      if (priceRange) {
        filtered = filtered.filter(product => {
          const price = parseFloat(product.price) || 0;
          return price >= priceRange.min && (priceRange.max === Infinity ? true : price <= priceRange.max);
        });
      }
    }

    // Apply GST filter
    if (selectedGstValue) {
      filtered = filtered.filter(product =>
        String(product.gst || '') === selectedGstValue
      );
    }

    // Apply type filter
    if (selectedType) {
      if (selectedType === 'standard') {
        filtered = filtered.filter(product => product.isStandard === true);
      } else if (selectedType === 'derived') {
        filtered = filtered.filter(product => product.isStandard === false);
      }
    }

    // Apply source filter
    if (selectedSource) {
      if (selectedSource === 'main') {
        filtered = filtered.filter(product => product.source === 'main_collection');
      } else if (selectedSource === 'sub') {
        filtered = [];
      }
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, selectedPriceRange, selectedGstValue, selectedType, selectedSource]);

  // Menu handlers
  const handlePriceMenuOpen = (event) => setPriceMenuAnchor(event.currentTarget);
  const handlePriceMenuClose = () => setPriceMenuAnchor(null);
  const handleGstMenuOpen = (event) => setGstMenuAnchor(event.currentTarget);
  const handleGstMenuClose = () => setGstMenuAnchor(null);
  const handleTypeMenuOpen = (event) => setTypeMenuAnchor(event.currentTarget);
  const handleTypeMenuClose = () => setTypeMenuAnchor(null);
  const handleSourceMenuOpen = (event) => setSourceMenuAnchor(event.currentTarget);
  const handleSourceMenuClose = () => setSourceMenuAnchor(null);

  // Filter selection handlers
  const handlePriceSelect = (priceRange) => {
    setSelectedPriceRange(priceRange);
    handlePriceMenuClose();
  };

  const handleGstSelect = (gst) => {
    setSelectedGstValue(gst);
    handleGstMenuClose();
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    handleTypeMenuClose();
  };

  const handleSourceSelect = (source) => {
    setSelectedSource(source);
    handleSourceMenuClose();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedPriceRange('');
    setSelectedGstValue('');
    setSelectedType('');
    setSelectedSource('');
    setSearchTerm('');
  };

  const stats = React.useMemo(() => {
  const total = products.length;
  const standard = products.filter(p => p.isStandard).length;
  const derived = products.filter(p => !p.isStandard).length;
  
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
  
  const addedInLast24Hours = products.filter(product => {
    if (!product.createdAt) return false;
    let productCreatedAt;
    if (product.createdAt && typeof product.createdAt.toDate === 'function') {
      productCreatedAt = product.createdAt.toDate();
    } else if (product.createdAt && product.createdAt.seconds) {
      productCreatedAt = new Date(product.createdAt.seconds * 1000);
    } else if (product.createdAt instanceof Date) {
      productCreatedAt = product.createdAt;
    } else if (typeof product.createdAt === 'string') {
      productCreatedAt = new Date(product.createdAt);
    } else {
      return false;
    }
    return productCreatedAt >= twentyFourHoursAgo;
  }).length;

  return { 
    total, 
    standard, 
    derived, 
    addedInLast24Hours
  };
}, [products]);

const ProductCard = React.memo(({ product }) => {
  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 120,
        border: '2px solid',
        border: '2px solid',
        borderColor: product.isStandard ? '#4CAF50' : '#FF9800',
        boxSizing: 'border-box',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        transition: ' border-color 0.2s ease-in-out',
        willChange: 'transform, box-shadow',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          borderColor: product.isStandard ? '#388E3C' : '#F57C00',
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Only show delete button for admin users */}
        {hasAdminAccess && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteProduct(product);
            }}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                color: 'error.main',
                transform: 'none',
              }
            }}
          >
            <DeleteIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
          </Box>
        )}

        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          mb: 1,
          pr: '36px', // Always reserve space for the delete button, prevents shake
          fontSize: '1.1rem',
          lineHeight: 1.2,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          userSelect: 'none',
          transition: 'padding 0s, margin 0s',
        }}>
          {product.name}
        </Typography>

        {!product.isStandard && product.components && product.components.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {product.components.map((component, index) => (
              <Typography key={index} variant="body2" sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  pl: 1,
                  display: 'block',
                  lineHeight: 1.3,
                  userSelect: 'none',
                  '&:before': { content: '"→ "', color: 'warning.main', fontWeight: 600 }
                }}>
                {component.name} (x{component.quantity})
              </Typography>
            ))}
          </Box>
        )}

        {product.isStandard && product.hasSubProducts && product.subProductNames && product.subProductNames.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {product.subProductNames.map((subName, index) => (
              <Typography key={index} variant="body2" sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  pl: 1,
                  display: 'block',
                  lineHeight: 1.3,
                  userSelect: 'none',
                  '&:before': { content: '"→ "', color: 'primary.main', fontWeight: 600 }
                }}>
                {subName}
              </Typography>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Grid container spacing={0.5}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, userSelect: 'none' }}>
                Price: <span style={{ color: '#4CAF50', fontWeight: 600 }}>₹{product.price || 'N/A'}</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, userSelect: 'none' }}>
                GST: <span style={{ fontWeight: 600 }}>{product.gst ? `${product.gst}%` : 'N/A'}</span>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, userSelect: 'none' }}>
                MOQ: <span style={{ fontWeight: 600 }}>{product.moq || 'N/A'}</span>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
});

const ProductListItem = React.memo(({ product }) => {
  return (
    <Paper sx={{
      p: 2,
      mb: 1,
      border: product.isStandard ? '1px solid #4CAF50' : '1px solid #FF9800',
      transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      willChange: 'background-color',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        borderColor: product.isStandard ? '#4CAF50' : '#FF9800',
      }
    }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: '1rem',
                mb: 0.5,
                lineHeight: 1.4,
                userSelect: 'none'
              }}>
                {product.name}
              </Typography>
              
              {!product.isStandard && product.components && product.components.length > 0 && (
                <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    mb: 1,
                    lineHeight: 1.4,
                    userSelect: 'none'
                  }}>
                  <span style={{ color: '#FF9800', fontWeight: 600 }}>Constituents: </span>
                  {product.components.map((component, index) => (
                    <span key={index}>
                      {component.name} (x{component.quantity})
                      {index < product.components.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </Typography>
              )}
              
              {product.isStandard && product.hasSubProducts && product.subProductNames && product.subProductNames.length > 0 && (
                <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    mb: 1,
                    lineHeight: 1.4,
                    userSelect: 'none'
                  }}>
                  <span style={{ color: '#1976d2', fontWeight: 600 }}>Sub-products: </span>
                  {product.subProductNames.join(', ')}
                </Typography>
              )}
            </Box>
            
            {/* Only show delete button for admin users */}
            {hasAdminAccess && (
              <Box sx={{ flexShrink: 0 }}>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product); }}
                    sx={{ width: 44, height: 44, transition: 'background-color 0.2s ease-in-out', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' } }}
                  >
                    <DeleteIcon sx={{ fontSize: '2.2rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, userSelect: 'none' }}>
              Price: <span style={{ color: '#4CAF50', fontWeight: 600 }}>₹{product.price || 'N/A'}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, userSelect: 'none' }}>
              GST: <span style={{ fontWeight: 600 }}>{product.gst ? `${product.gst}%` : 'N/A'}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, userSelect: 'none' }}>
              MOQ: <span style={{ fontWeight: 600 }}>{product.moq || 'N/A'}</span>
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Chip label={product.isStandard ? 'Standard' : 'Derived'} size="small" color={product.isStandard ? 'success' : 'warning'} variant="outlined" />
              {product.hasSubProducts && (<Chip label="Has Sub-Products" size="small" color="info" variant="outlined" />)}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
});


const BulkImportDialog = () => {
  return (
    <Dialog open={bulkDialogOpen} onClose={handleBulkClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Bulk Import Products</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={unifiedImportMode ? 'contained' : 'outlined'}
              onClick={() => setUnifiedImportMode(true)}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Unified Import
            </Button>
            <Button
              variant={!unifiedImportMode && importMode === 'standard' ? 'contained' : 'outlined'}
              onClick={() => { setUnifiedImportMode(false); setImportMode('standard'); }}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Standard Only
            </Button>
            <Button
              variant={!unifiedImportMode && importMode === 'derived' ? 'contained' : 'outlined'}
              onClick={() => { setUnifiedImportMode(false); setImportMode('derived'); }}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Derived Only
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {unifiedImportMode ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Import both <strong>standard and derived products</strong> in a single Excel file. 
            Standard products will be processed first, followed by derived products in dependency order.
            Derived product prices are auto-calculated from component prices.
          </Alert>
        ) : importMode === 'standard' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Import new <strong>standard products</strong> that will serve as base components for derived products.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Import new <strong>derived products</strong> made from combinations of existing standard and derived products. 
            Price will be auto-calculated from component prices.
          </Alert>
        )}

        <Typography variant="subtitle1" gutterBottom>
          Template Example for {unifiedImportMode ? 'Unified' : importMode === 'standard' ? 'Standard' : 'Derived'} Products:
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {unifiedImportMode ? (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Product_Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Margin</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>GST</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>HSN</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>MOQ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Component1_Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Component1_Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>...</TableCell>
                  </>
                ) : importMode === 'standard' ? (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Margin</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>GST</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>HSN</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>MOQ</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Margin</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>GST</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>HSN</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>MOQ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Component1_Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Component1_Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>...</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
                  {unifiedImportMode ? (
                <>
                  <TableRow>
                    <TableCell>standard</TableCell>
                    <TableCell>Steel Pipe</TableCell>
                        <TableCell>High-quality steel pipe</TableCell>
                        <TableCell>120</TableCell>
                        <TableCell>25</TableCell>
                        <TableCell>150</TableCell>
                        <TableCell>18</TableCell>
                        <TableCell>7306</TableCell>
                        <TableCell>10</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>...</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>derived</TableCell>
                    <TableCell>Custom Assembly</TableCell>
                        <TableCell>Assembly from pipes</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>18</TableCell>
                        <TableCell>7306</TableCell>
                        <TableCell>5</TableCell>
                    <TableCell>Steel Pipe</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>...</TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  {importMode === 'standard' ? (
                    <>
                      <TableCell>Steel Pipe</TableCell>
                      <TableCell>High-quality steel pipe</TableCell>
                      <TableCell>120</TableCell>
                      <TableCell>25</TableCell>
                      <TableCell>150</TableCell>
                      <TableCell>18</TableCell>
                      <TableCell>7306</TableCell>
                      <TableCell>10</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>Custom Assembly A</TableCell>
                      <TableCell>Assembly made from pipes and fittings</TableCell>
                      <TableCell>18</TableCell>
                      <TableCell>7306</TableCell>
                      <TableCell>5</TableCell>
                      <TableCell>Steel Pipe</TableCell>
                      <TableCell>2</TableCell>
                      <TableCell>...</TableCell>
                    </>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!unifiedImportMode && importMode === 'derived' && availableProducts.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Available Products for Components ({availableProducts.length} products)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                <Grid container spacing={1}>
                  {availableProducts.map((product) => (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <Chip
                        label={`${product.name} (₹${product.price})`}
                        color={product.isStandard ? 'success' : 'warning'}
                        variant="outlined"
                        size="small"
                        sx={{ width: '100%', justifyContent: 'flex-start' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            <Typography fontWeight="bold">{successMessage}</Typography>
          </Alert>
        )}
        
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Please fix these errors:</Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, maxHeight: 200, overflow: 'auto' }}>
              {errors.map((errorMsg, i) => (
                <Typography component="li" key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {errorMsg}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleDownloadTemplate} variant="outlined">
          Download {unifiedImportMode ? 'Unified' : importMode === 'standard' ? 'Standard' : 'Derived'} Template
        </Button>
        <input 
          type="file" 
          accept=".xlsx,.xls,.csv" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          onChange={handleFileUpload} 
        />
        <Button variant="contained" onClick={() => fileInputRef.current?.click()}>
          Upload & Process
        </Button>
        <Button onClick={handleBulkClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Determine the effective view mode based on screen size
const effectiveViewMode = isMobile ? 'list' : viewMode;

// --- (isUserRoleLoading check REMOVED. The page will now load immediately) ---

// --- (RoleDebugInfo and UserRoleDebug components REMOVED) ---

return (
  <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
    {/* ==================================================================== */}
    {/* PAGE HEADER - NOW RESPONSIVE WITH ROLE-BASED ACCESS */}
    {/* ==================================================================== */}
    <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3
    }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.8rem', sm: '2.125rem' } }}>
          All the saved products...
        </Typography>
        
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
  
  {/* Show admin buttons only when user has admin access */}
  {hasAdminAccess && (
    <>
      <Tooltip title="Bulk Import">
        {false ? (
          <span style={{ display: 'inline-flex' }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleBulkOpen}
              sx={{ textTransform: 'none' }}
              disabled
            >
              Bulk Import
            </Button>
          </span>
        ) : (
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleBulkOpen}
            sx={{ textTransform: 'none' }}
          >
            Bulk Import
          </Button>
        )}
      </Tooltip>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddProductClick}
        sx={{ textTransform: 'none' }}
      >
        Add Product
      </Button>
    </>
  )}

  {/* Show disabled buttons for non-admin users */}
  {/* --- 7. SIMPLIFIED LOGIC --- */}
  {!hasAdminAccess && (
    <>
      <span style={{ display: 'inline-flex' }}>
        <Button
          variant="outlined"
          disabled
          startIcon={<UploadFileIcon />}
          sx={{ textTransform: 'none', opacity: 0.4 }}
          title="Admin access required"
        >
          Bulk Import
        </Button>
      </span>
      <span style={{ display: 'inline-flex' }}>
        <Button
          variant="outlined"
          disabled
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none', opacity: 0.4 }}
          title="Admin access required"
        >
          Add Product
        </Button>
      </span>
    </>
  )}

  {/* (Loading permissions button REMOVED) */}

  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
  
  <Tooltip title="Refresh products">
    {loading ? (
      <span style={{ display: 'inline-flex' }}>
        <IconButton onClick={fetchAllProducts} disabled>
          <RefreshIcon />
        </IconButton>
      </span>
    ) : (
      <IconButton onClick={fetchAllProducts}>
        <RefreshIcon />
      </IconButton>
    )}
  </Tooltip>
  
  <Tooltip title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}>
    {false ? ( // If you ever disable this button, wrap in span
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          disabled
        >
          {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
        </IconButton>
      </span>
    ) : (
      <IconButton
        onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
        sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
      >
        {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
      </IconButton>
    )}
  </Tooltip>
</Box>
    </Box>


    {/* Role-based alert message */}
    <Alert 
      severity={hasAdminAccess ? "info" : "warning"} 
      sx={{ mb: 2 }}
    >
      <Typography variant="body2">
        {hasAdminAccess 
          ? "You have administrator privileges." 
          : "You are viewing products in read-only mode. Contact your administrator for editing permissions."
        }
      </Typography>
    </Alert>
    

    {/* Only show delete confirmation dialog for admins */}
    {hasAdminAccess && (
      <Dialog open={showConfirmDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This action cannot be undone!</Alert>
          <Typography>
            Are you sure you want to delete the Level 1 product "{productToDelete?.name}"? This will permanently remove it from your catalogue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting} sx={{ textTransform: 'none' }}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    )}

    {/* Only render bulk import dialog for admins */}
    {hasAdminAccess && <BulkImportDialog />}

    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={3}><Card sx={{ textAlign: 'center' }}><CardContent sx={{ py: 2 }}><Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>{stats.total}</Typography><Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>All Products</Typography></CardContent></Card></Grid>
      <Grid item xs={6} sm={3}><Card sx={{ textAlign: 'center' }}><CardContent sx={{ py: 2 }}><Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>{stats.standard}</Typography><Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Standard Products</Typography></CardContent></Card></Grid>
      <Grid item xs={6} sm={3}><Card sx={{ textAlign: 'center' }}><CardContent sx={{ py: 2 }}><Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>{stats.derived}</Typography><Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Derived Products</Typography></CardContent></Card></Grid>
      <Grid item xs={6} sm={3}><Card sx={{ textAlign: 'center' }}><CardContent sx={{ py: 2 }}><Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>{stats.addedInLast24Hours}</Typography><Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Products Added in Last 24hrs</Typography></CardContent></Card></Grid>
    </Grid>
    

    <Paper sx={{ p: 1.5, mb: 2 }}>
      <TextField fullWidth placeholder="Search Level 1 products by name, description, GST, HSN, price..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>), endAdornment: searchTerm && (<InputAdornment position="end"><IconButton onClick={() => setSearchTerm('')} size="small"><ClearIcon /></IconButton></InputAdornment>) }} sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button variant={selectedPriceRange ? 'contained' : 'outlined'} endIcon={<ArrowDropDownIcon />} onClick={handlePriceMenuOpen} sx={{ borderRadius: '20px', textTransform: 'none' }}>Price {selectedPriceRange && `(${priceRanges.find(p => p.value === selectedPriceRange)?.label})`}</Button>
        <Button variant={selectedGstValue ? 'contained' : 'outlined'} endIcon={<ArrowDropDownIcon />} onClick={handleGstMenuOpen} sx={{ borderRadius: '20px', textTransform: 'none' }}>GST {selectedGstValue && `(${selectedGstValue}%)`}</Button>
        <Button variant={selectedType ? 'contained' : 'outlined'} endIcon={<ArrowDropDownIcon />} onClick={handleTypeMenuOpen} sx={{ borderRadius: '20px', textTransform: 'none' }}>Type {selectedType && `(${selectedType === 'standard' ? 'Standard' : 'Derived'})`}</Button>
        <Button variant={selectedSource ? 'contained' : 'outlined'} endIcon={<ArrowDropDownIcon />} onClick={handleSourceMenuOpen} sx={{ borderRadius: '20px', textTransform: 'none' }}>Source {selectedSource && `(${selectedSource === 'main' ? 'Main' : 'Sub'})`}</Button>
        {(selectedPriceRange || selectedGstValue || selectedType || selectedSource || searchTerm) && (<Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={clearAllFilters} sx={{ borderRadius: '20px', textTransform: 'none' }}>Clear All</Button>)}
      </Box>
      {(selectedPriceRange || selectedGstValue || selectedType || selectedSource) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ alignSelf: 'center', fontWeight: 600 }}>Active filters:</Typography>
          {selectedPriceRange && (<Chip label={`Price: ${priceRanges.find(p => p.value === selectedPriceRange)?.label}`} size="small" onDelete={() => handlePriceSelect('')} color="primary" />)}
          {selectedGstValue && (<Chip label={`GST: ${selectedGstValue}%`} size="small" onDelete={() => handleGstSelect('')} color="primary" />)}
          {selectedType && (<Chip label={`Type: ${selectedType === 'standard' ? 'Standard' : 'Derived'}`} size="small" onDelete={() => handleTypeSelect('')} color={selectedType === 'standard' ? 'success' : 'warning'} />)}
          {selectedSource && (<Chip label={`Source: ${selectedSource === 'main' ? 'Main' : 'Sub'}`} size="small" onDelete={() => handleSourceSelect('')} color={selectedSource === 'main' ? 'primary' : 'secondary'} />)}
        </Box>
      )}
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Showing {filteredProducts.length} of {stats.total} Level 1 products</Typography>
    </Paper>

    <Menu anchorEl={priceMenuAnchor} open={Boolean(priceMenuAnchor)} onClose={handlePriceMenuClose} PaperProps={{ sx: { minWidth: '200px' } }}><MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>Select Price Range</MenuItem><Divider />{priceRanges.map((range) => (<MenuItem key={range.value} onClick={() => handlePriceSelect(range.value)} selected={selectedPriceRange === range.value}><AttachMoneyIcon fontSize="small" sx={{ mr: 1 }} />{range.label}</MenuItem>))}{selectedPriceRange && (<><Divider /><MenuItem onClick={() => handlePriceSelect('')} sx={{ color: 'error.main' }}>Clear Price Filter</MenuItem></>)}</Menu>
    <Menu anchorEl={gstMenuAnchor} open={Boolean(gstMenuAnchor)} onClose={handleGstMenuClose} PaperProps={{ sx: { minWidth: '200px' } }}><MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>Select GST Rate</MenuItem><Divider />{gstOptions.map((gst) => (<MenuItem key={gst.value} onClick={() => handleGstSelect(gst.value)} selected={selectedGstValue === gst.value}><ReceiptIcon fontSize="small" sx={{ mr: 1 }} />{gst.label}</MenuItem>))}{selectedGstValue && (<><Divider /><MenuItem onClick={() => handleGstSelect('')} sx={{ color: 'error.main' }}>Clear GST Filter</MenuItem></>)}</Menu>
    <Menu anchorEl={typeMenuAnchor} open={Boolean(typeMenuAnchor)} onClose={handleTypeMenuClose} PaperProps={{ sx: { minWidth: '200px' } }}><MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>Select Product Type</MenuItem><Divider />{typeOptions.map((type) => (<MenuItem key={type.value} onClick={() => handleTypeSelect(type.value)} selected={selectedType === type.value}>{type.value === 'standard' ? (<VerifiedIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />) : (<CallSplitIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />)}{type.label}</MenuItem>))}{selectedType && (<><Divider /><MenuItem onClick={() => handleTypeSelect('')} sx={{ color: 'error.main' }}>Clear Type Filter</MenuItem></>)}</Menu>
    <Menu anchorEl={sourceMenuAnchor} open={Boolean(sourceMenuAnchor)} onClose={handleSourceMenuClose} PaperProps={{ sx: { minWidth: '200px' } }}><MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>Select Source</MenuItem><Divider />{sourceOptions.map((source) => (<MenuItem key={source.value} onClick={() => handleSourceSelect(source.value)} selected={selectedSource === source.value}>{source.value === 'main' ? (<StorageIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />) : (<AccountTreeIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />)}{source.label}</MenuItem>))}{selectedSource && (<><Divider /><MenuItem onClick={() => handleSourceSelect('')} sx={{ color: 'error.main' }}>Clear Source Filter</MenuItem></>)}</Menu>
    
    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading Level 1 products from Firestore...</Typography>
      </Box>
    ) : (
      <>
        {filteredProducts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>No Level 1 products found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || selectedPriceRange || selectedGstValue || selectedType || selectedSource
                ? 'Try adjusting your search criteria or filters'
                : 'No Level 1 products available.'}
            </Typography>
            {(searchTerm || selectedPriceRange || selectedGstValue || selectedType || selectedSource) && (
              <Button variant="outlined" onClick={clearAllFilters} startIcon={<ClearIcon />}>Clear All Filters</Button>
            )}
          </Paper>
        ) : (
          <>
            {/* Use the effectiveViewMode for rendering */}
            {effectiveViewMode === 'grid' ? (
              <Grid container spacing={2}>
                {filteredProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={2} key={product.id}>
                    <ProductCard product={product} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box>
                {filteredProducts.map((product) => (
                  <ProductListItem key={product.id} product={product} />
                ))}
              </Box>
            )}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Displaying all {filteredProducts.length} Level 1 products
              </Typography>
            </Box>
          </>
        )}
      </>
    )}
  </Box>
);
};

export default ViewProducts;